/* eslint-disable no-nested-ternary */
/* global React */
import { useState, useCallback, useEffect, useTransition } from 'react'
import { RadioGroup, FormControl, FormLabel, Grid, FormHelperText } from '@mui/material'
import PlatformEvent from 'lib/util/event'
import useMemoRef from '@platform/react/hook/useMemoRef'
import { useStyles } from '@platform/react/hook'
import Radio from 'ui/Element/Field/Radio'

const styles = theme => ({
  helperText: {
    margin: 0,
  },
  ...theme.custom.radioGroupField,
})

/**
 * Radio Group component.
 *
 * @param {Object} forwardedRef                         a reference to the root element
 * @param {Object} events                               component's event handlers
 * @param {function} [events.onChange]                  triggered whenever the selection changes
 * @param {function} [events.getSelection]              triggered during the first render as an
 *                                                      alternative way to obtain `options`
 * @param {string} label                                the field's label
 * @param {boolean} checked                             whether the component is checked
 * @param {'row'|'column'|'inline'|'default'} [variant] display style variant
 * @param {Object} props                                additional component's properties
 * @param {string} props.value                          the component's current value
 * @param {Object[]} props.options                      a list of options to display
 * @param {boolean} props.disabled                      whether to render the group as disabled
 * @param {boolean} props.error                         whether the component is in error state
 * @param {string} props.helperText                     additional information text
 * @returns {JSX.Element}
 */
const RadioGroupField = ({ forwardedRef, events, label, checked, variant = 'default', ...props }) => {
  const [, startTransition] = useTransition()
  const classes = useStyles(styles)()
  const [value, setValue] = useState(props.value || '')
  const [options, setOptions] = useState(props.options || [])
  const row = !variant || variant === 'row'
  const column = variant === 'column'
  const inline = variant === 'inline'
  const standard = variant === 'default'

  const count = options.filter(option => !option.hidden).length
  const childWidth = (row && 12 / (count ? count % 12 : 1))
    || (column && 12)
    || (inline && 'auto')
    || (standard && 'auto')

  // Event handler utilizing useCallback ...
  // ... so that reference never changes.
  const handleClick = useCallback((event) => {
    const { value: newValue, checked: isChecked } = event.target
    setValue(newValue)
    startTransition(() => {
      events?.onChange(new PlatformEvent(event, { value: newValue, checked: isChecked }))
    })
  }, [events.onChange])

  // Allowing options to be set through a getSelection handler
  useEffect(() => {
    (async () => {
      const newOptions = await events?.getSelection?.(null)
      newOptions?.length && setOptions(newOptions)
    })()
  }, [])

  useEffect(() => {
    setOptions(props.options || options || [])
  }, [props.options])

  useEffect(() => {
    setValue(props.value)
  }, [props.value])

  return (
    <Grid
      item
      container
      ref={forwardedRef}
      component={FormControl}
      error={props.error}
      xs={12}
    >
      <Grid
        item
        container
        justifyContent={'flex-start'}
        alignItems={'center'}
        alignContent={'center'}
        flexWrap={inline ? 'nowrap' : 'wrap'}
        spacing={inline || row ? 2 : 0}
        xs={'auto'}
      >
        {!label ? null : (
          <Grid
            item
            component={componentProps => (
              <FormLabel {...componentProps} component={'legend'} />
            )}
            xs={inline ? 'auto' : 12}
          >
            {label}
          </Grid>
        )}
        <Grid
          item
          container
          component={RadioGroup}
          value={value ?? null}
          aria-label={label}
          flexWrap={inline ? 'nowrap' : 'wrap'}
          xs={'auto'}
        >
          {options.reduce((acc, option) => {
            !option.hidden && acc.push(
              <Grid
                key={option.key}
                item
                sm={childWidth}
                xs={12}
              >
                <Radio
                  key={option.key}
                  label={option.value}
                  value={option.key}
                  onClick={handleClick}
                  icon={option.icon}
                  checkedIcon={option.icon}
                  iconColor={option.color}
                  disabled={option.disabled || props.disabled}
                  checked={option.key === value}
                />
              </Grid>,
            )
            return acc
          }, [])}
        </Grid>
      </Grid>
      {props.helperText !== null && (
        <Grid
          item
          component={FormHelperText}
          className={classes.helperText}
          xs={'auto'}
        >
          {props.helperText || '\u00A0'}
        </Grid>
      )}
    </Grid>
  )
}

export default useMemoRef(RadioGroupField, props => [
  props.value, props.variant, props.error, props.options, props.disabled,
])
